import {useState, useMemo, useCallback} from 'react'
import {FilterModel} from '../../../models/FilterModel'
import {SearchResultModel} from '../../../models/SearchResultModel'
import {useDebounce} from '../../hooks/useDebounce'
import {CancelledPromiseError, usePromiseManager} from '../../hooks/usePromiseManager'

interface UseControlledFilterTableApiOptions<T> {
  debounce?: number
  onFilter: (filters: FilterModel) => Promise<SearchResultModel<T>>
  initialFilters?: FilterModel
}

export const useControlledFilterTableApi = <T extends Record<string, any>>({
  onFilter: onFilterCallback,
  debounce = 500,
  initialFilters = {},
}: UseControlledFilterTableApiOptions<T>) => {
  const [searchResults, setSearchResults] = useState<SearchResultModel<T>>()
  const [filters, setFilters] = useState<FilterModel>(initialFilters)
  const {managePromise, isLoading} = usePromiseManager()
  const refreshDataDebounce = useDebounce(debounce)

  const refresh = useCallback(() => {
    refreshDataDebounce(async () => {
      try {
        const data = await managePromise('filter', onFilterCallback(filters))
        setSearchResults(data)
      } catch (e) {
        if (!(e instanceof CancelledPromiseError)) {
          // TODO: Add error handlers.
        }
      }
    })
  }, [filters, managePromise, onFilterCallback, refreshDataDebounce])

  const onFilter = useCallback(
    async (filters: FilterModel) => {
      setFilters(filters)
      refreshDataDebounce(async () => {
        try {
          const data = await managePromise('filter', onFilterCallback(filters))
          setSearchResults(data)
        } catch (e) {
          if (!(e instanceof CancelledPromiseError)) {
            // TODO: Add error handlers.
          }
        }
      })
    },
    [managePromise, onFilterCallback, refreshDataDebounce]
  )

  return useMemo(
    () => ({
      onFilter,
      filters,
      searchResults,
      onRefresh: refresh,
      isLoading,
      initialFilters,
      refresh,
    }),
    [onFilter, filters, searchResults, refresh, isLoading, initialFilters]
  )
}
